import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { WorkbenchField, WorkbenchNotice } from '@ndrc/ui';
import { callCore, mutateCore, uploadPracticeFile } from '../lib/rpc';
import { formatFileSize } from '../documents/fileSize';
import { t } from '../messages';
import type { PracticeFile } from './model';

export function PracticeFileLinks({ files }: { files: PracticeFile[] }) {
  if (!files.length) return null;
  return (
    <section aria-label={t('practice.files.title')}>
      <h3>{t('practice.files.title')}</h3>
      <ul className="practice-sources practice-file-list">
        {files.map((file) => (
          <li key={file.id}>
            <span>
              {file.url ? (
                <a href={file.url} target="_blank" rel="noreferrer">
                  {file.name}
                </a>
              ) : (
                file.name
              )}
            </span>
            <span>{formatFileSize(file.sizeBytes)}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}
export function PracticeAnnexFiles({
  classId,
  selected,
  onChange,
  onBusyChange,
}: {
  classId: string;
  selected: string[];
  onChange: (ids: string[]) => void;
  onBusyChange: (busy: boolean) => void;
}) {
  const [busy, setBusy] = useState(false),
    [error, setError] = useState(''),
    [notice, setNotice] = useState('');
  const query = useQuery({
    queryKey: ['core', 'practice.files.list', classId],
    queryFn: () =>
      callCore<{
        files: PracticeFile[];
        limits: { accept: string; maxBytes: number; maxFilesPerSubject: number };
      }>('practice.files.list', { classId }),
    retry: false,
    refetchInterval: 60000,
  });
  const files = query.data?.files ?? [],
    limits = query.data?.limits;
  const missing = query.isSuccess && selected.some((id) => !files.some((file) => file.id === id));
  async function work(action: () => Promise<void>) {
    setBusy(true);
    onBusyChange(true);
    setError('');
    setNotice('');
    try {
      await action();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('practice.error'));
    } finally {
      await query.refetch();
      setBusy(false);
      onBusyChange(false);
    }
  }
  return (
    <section className="practice-card" aria-label={t('practice.files.manage')}>
      <h3>{t('practice.files.manage')}</h3>
      <p>{t('practice.files.description')}</p>
      {(error || query.isError) && (
        <WorkbenchNotice error>{error || query.error?.message}</WorkbenchNotice>
      )}
      {notice && <WorkbenchNotice>{notice}</WorkbenchNotice>}
      {missing && (
        <WorkbenchNotice error>
          <p>{t('practice.files.missing')}</p>
          <button
            type="button"
            disabled={busy}
            onClick={() => onChange(selected.filter((id) => files.some((file) => file.id === id)))}
          >
            {t('practice.files.removeMissing')}
          </button>
        </WorkbenchNotice>
      )}
      <WorkbenchField label={t('practice.files.upload')}>
        <input
          type="file"
          accept={limits?.accept}
          disabled={busy || !limits || query.isError}
          onChange={(event) => {
            const input = event.currentTarget,
              file = input.files?.[0];
            if (!file) return;
            void work(async () => {
              if (file.size > (limits?.maxBytes ?? 0))
                throw new Error(
                  t('practice.files.tooLarge', { size: formatFileSize(limits?.maxBytes ?? 0) }),
                );
              const result = await uploadPracticeFile(classId, file);
              setNotice(
                t(result.deduplicated ? 'practice.files.duplicate' : 'practice.files.uploaded'),
              );
              input.value = '';
            });
          }}
        />
      </WorkbenchField>
      {limits && (
        <p>
          {t('practice.files.limits', {
            size: formatFileSize(limits.maxBytes),
            count: limits.maxFilesPerSubject,
          })}
        </p>
      )}
      <div className="lab-actions">
        <strong>{t('practice.files.selected', { count: selected.length })}</strong>
        <button
          type="button"
          disabled={busy || query.isFetching}
          onClick={() => {
            void query.refetch();
          }}
        >
          {t('practice.refresh')}
        </button>
      </div>
      {query.isPending && <p role="status">{t('practice.loading')}</p>}
      {query.isSuccess && !files.length && <p>{t('practice.files.empty')}</p>}
      <ul className="practice-sources practice-file-list">
        {files.map((file) => (
          <li key={file.id}>
            <label>
              <input
                type="checkbox"
                checked={selected.includes(file.id)}
                disabled={
                  busy ||
                  (!selected.includes(file.id) &&
                    selected.length >= (limits?.maxFilesPerSubject ?? 0))
                }
                onChange={(event) =>
                  onChange(
                    event.target.checked
                      ? [...selected, file.id]
                      : selected.filter((id) => id !== file.id),
                  )
                }
              />
              <span>{file.name}</span>
            </label>
            <span>{formatFileSize(file.sizeBytes)}</span>
            {file.url && (
              <a href={file.url} target="_blank" rel="noreferrer">
                {t('practice.files.open')}
              </a>
            )}
            {file.used ? (
              <span>{t('practice.files.retained')}</span>
            ) : (
              <button
                type="button"
                disabled={busy || selected.includes(file.id)}
                onClick={() => {
                  void work(async () => {
                    await mutateCore('practice.files.remove', { classId, fileId: file.id });
                    setNotice(t('practice.files.removed'));
                  });
                }}
              >
                {t('practice.files.remove')}
              </button>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
